import { Controller } from "@hotwired/stimulus"

// Drives the voice-to-application modal.
//
// Flow:
//   idle → recording → idle (transcript grows) → processing → (followUp →…)* → redirect
//
// The user taps the mic to record a phrase. Recognition ends naturally when
// the browser detects silence. The transcript accumulates across multiple taps.
// When ready, the user taps Send.
//
// State is kept entirely on the client. `conversation` accumulates the full
// dialogue history; `extractedJson` holds whatever Claude has extracted so far.
// Both are sent to the server on every POST.
//
// Values:
//   url — the process_voice endpoint
//
// Targets:
//   dialog      — the <dialog> element
//   status      — status line
//   prompt      — follow-up question text shown to the user
//   transcript  — accumulated transcript display
//   micButton   — tap to record
//   sendButton  — tap to send transcript to Claude
//   textSection — wrapper for the text fallback input + submit
//   textInput   — text fallback <input>
//   spinner     — processing indicator

export default class extends Controller {
  static targets = [
    "dialog", "status", "prompt", "transcript", "transcriptText", "continueHint", "spellHint",
    "micButton", "sendButton",
    "textSection", "textInput",
    "spinner"
  ]
  static values = { url: String }

  connect() {
    this._conversation = []
    this._extractedJson = {}
    this._recognition = null
    this._state = "idle"
    this._accumulated = "" // transcript across all taps
  }

  // ── Public actions ─────────────────────────────────────────────────────────

  open() {
    this._conversation = []
    this._extractedJson = {}
    this._sessionId = crypto.randomUUID()
    this._enterIdle()
    this.dialogTarget.showModal()
  }

  close() {
    this._stopRecognition()
    this.dialogTarget.close()
    this._enterIdle()
  }

  backdropClick(event) {
    const box = this.dialogTarget.querySelector(".modal-box")
    if (box && !box.contains(event.target)) this.close()
  }

  record() {
    if (this._state === "recording") {
      // Tap again to stop early
      this._recognition?.stop()
      return
    }
    if (this._state === "processing" || this._state === "done") return

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SR) {
      this._showStatus("Speech not supported in this browser. Use the text box below.")
      this._showTextSection()
      return
    }
    if (!window.isSecureContext) {
      this._showStatus("Microphone requires HTTPS. Use the text box below.")
      this._showTextSection()
      return
    }

    const recognition = new SR()
    recognition.continuous = false
    recognition.interimResults = true
    recognition.lang = "en-US"

    let sessionFinal = ""

    recognition.onresult = (event) => {
      sessionFinal = ""
      let interim = ""
      for (let i = 0; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          sessionFinal += event.results[i][0].transcript + " "
        } else {
          interim += event.results[i][0].transcript
        }
      }
      this._updateTranscript(this._accumulated + sessionFinal, interim)
    }

    recognition.onerror = (event) => {
      if (event.error === "no-speech" || event.error === "aborted") return
      this._stopRecognition()
      this._showStatus("Microphone error. Use the text box below.")
      this._showTextSection()
      this._enterReady()
    }

    recognition.onend = () => {
      this._recognition = null
      if (sessionFinal) this._accumulated += sessionFinal
      this._enterReady()
    }

    this._recognition = recognition
    this._state = "recording"
    this._showMicRecording()
    this._showStatus("Listening...")
    recognition.start()
  }

  send() {
    const text = this._accumulated.trim()
    if (!text) return
    this._sendToServer(text)
  }

  submitText(event) {
    event.preventDefault()
    const text = this.textInputTarget.value.trim()
    if (!text) return
    this.textInputTarget.value = ""
    this._sendToServer(text)
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  _stopRecognition() {
    if (this._recognition) {
      this._recognition.abort()
      this._recognition = null
    }
  }

  // ── Server communication ───────────────────────────────────────────────────

  async _sendToServer(transcript) {
    this._state = "processing"
    this._enterProcessing()

    this._conversation.push({ role: "user", content: transcript })

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          conversation: this._conversation,
          extracted_json: this._extractedJson,
          session_id: this._sessionId
        })
      })

      if (response.status === 429) {
        const data = await response.json().catch(() => ({}))
        this._enterLimitReached(data.error || "You've reached the daily voice limit. Try again tomorrow.")
        return
      }

      if (!response.ok) throw new Error(`Server error ${response.status}`)

      const data = await response.json()

      if (data.extracted) this._extractedJson = data.extracted

      if (data.redirect_url) {
        this._enterDone()
        window.location.href = data.redirect_url
      } else if (data.follow_up_prompt) {
        this._conversation.push({ role: "assistant", content: data.follow_up_prompt })
        this._enterFollowUp(data.follow_up_prompt)
      } else {
        this._enterFollowUp("Something went wrong. Please try again.")
      }
    } catch (err) {
      this._enterFollowUp("Something went wrong. Please try again.")
    }
  }

  // ── State transitions ──────────────────────────────────────────────────────

  _enterIdle() {
    this._state = "idle"
    this._lastPrompt = null
    this._accumulated = ""
    this._showStatus("Tap the mic and describe the application.")
    this._showMicIdle()
    this._hideSendButton()
    this._hideContinueHint()
    this._hideSpellHint()
    this._hideSpinner()
    this._hideTextSection()
    this._clearPrompt()
    this._clearTranscript()

  }

  // After a recording session ends — show what we have, let user record more or send
  _enterReady() {
    this._state = this._lastPrompt ? "followUp" : "idle"
    this._showMicIdle()
    this._hideSpinner()
    if (this._accumulated.trim()) {
      this._showStatus("")
      this._showContinueHint()
      this._showSendButton()
      this._showSpellHint()
    } else {
      this._hideContinueHint()
      this._hideSpellHint()
      this._showStatus(this._lastPrompt ? "" : "Tap the mic and describe the application.")
    }
  }

  _enterProcessing() {
    this._showStatus("Processing...")
    this._showMicIdle()
    this._hideSendButton()
    this._hideContinueHint()
    this._hideSpellHint()
    this._showSpinner()
    this._hideTextSection()
    this._clearTranscript()
  }

  _enterFollowUp(prompt) {
    this._state = "followUp"
    this._lastPrompt = prompt
    this._accumulated = ""
    this._showStatus("")
    this._showPrompt(prompt)
    this._showMicIdle()
    this._hideSendButton()
    this._hideContinueHint()
    this._hideSpellHint()
    this._hideSpinner()
    this._showTextSection()
    this._clearTranscript()
  }

  _enterDone() {
    this._state = "done"
    this._showStatus("Got it. Taking you to the record now...")
    this._showMicIdle()
    this._hideSendButton()
    this._hideContinueHint()
    this._hideSpellHint()
    this._hideSpinner()
    this._hideTextSection()
    this._clearTranscript()
  }

  // ── DOM helpers ────────────────────────────────────────────────────────────

  _showStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }

  _showPrompt(text) {
    if (this.hasPromptTarget) {
      this.promptTarget.textContent = text
      this.promptTarget.classList.remove("hidden")
    }
  }

  _clearPrompt() {
    if (this.hasPromptTarget) {
      this.promptTarget.textContent = ""
      this.promptTarget.classList.add("hidden")
    }
  }

  _updateTranscript(final, interim) {
    if (!this.hasTranscriptTarget) return
    this.transcriptTextTarget.innerHTML =
      this._escape(final) +
      (interim ? `<span class="opacity-40">${this._escape(interim)}</span>` : "")
    this.transcriptTarget.classList.remove("hidden")
  }

  _clearTranscript() {
    if (!this.hasTranscriptTarget) return
    this.transcriptTextTarget.innerHTML = ""
    this.transcriptTarget.classList.add("hidden")
  }

  _showMicIdle() {
    if (this.hasMicButtonTarget) {
      this.micButtonTarget.classList.remove("btn-error")
      this.micButtonTarget.classList.add("btn-primary")
    }
  }

  _showMicRecording() {
    if (this.hasMicButtonTarget) {
      this.micButtonTarget.classList.remove("btn-primary")
      this.micButtonTarget.classList.add("btn-error")
    }
  }

  _showSendButton() {
    if (this.hasSendButtonTarget) this.sendButtonTarget.classList.remove("hidden")
  }

  _hideSendButton() {
    if (this.hasSendButtonTarget) this.sendButtonTarget.classList.add("hidden")
  }

  _showContinueHint() {
    if (this.hasContinueHintTarget) this.continueHintTarget.classList.remove("hidden")
  }

  _hideContinueHint() {
    if (this.hasContinueHintTarget) this.continueHintTarget.classList.add("hidden")
  }

  _showSpellHint() {
    if (this.hasSpellHintTarget) this.spellHintTarget.classList.remove("hidden")
  }

  _hideSpellHint() {
    if (this.hasSpellHintTarget) this.spellHintTarget.classList.add("hidden")
  }

  _showSpinner() {
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.remove("hidden")
  }

  _hideSpinner() {
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.add("hidden")
  }

  _showTextSection() {
    if (this.hasTextSectionTarget) this.textSectionTarget.classList.remove("hidden")
  }

  _hideTextSection() {
    if (this.hasTextSectionTarget) this.textSectionTarget.classList.add("hidden")
  }

  _escape(str) {
    return str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
  }
}
